import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  FiArrowLeft, 
  FiEdit2, 
  FiTrash2, 
  FiFolder, 
  FiLayers, 
  FiInfo, 
  FiCheck, 
  FiX 
} from "react-icons/fi";
import useAuth from "../../useAuth";

export default function CategoryManager() {
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Form States
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [nameError, setNameError] = useState("");
  
  // Edit Mode States
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/categories/all`, {
        headers: {
          Authorization: `Bearer ${token || localStorage.getItem("token")}`,
        }
      });
      setCategories(data?.data || []);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.message || "Failed to load categories");
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError("Category name is required");
      return;
    }
    setNameError("");
    setSubmitLoading(true);

    try {
      if (isEditMode) {
        // Update category
        const { data } = await axios.patch(
          `${server}/categories/update`,
          {
            id: editId,
            name: name.trim(),
            description: description.trim()
          },
          {
            headers: {
              Authorization: `Bearer ${token || localStorage.getItem("token")}`,
              "Content-Type": "application/json"
            }
          }
        );
        toast.success(data?.message || "Category updated successfully!");
        cancelEdit();
      } else {
        // Create category
        const { data } = await axios.post(
          `${server}/categories/new`,
          {
            name: name.trim(),
            description: description.trim()
          },
          {
            headers: {
              Authorization: `Bearer ${token || localStorage.getItem("token")}`,
              "Content-Type": "application/json"
            }
          }
        );
        toast.success(data?.message || "Category created successfully!");
        setName("");
        setDescription("");
      }
      fetchCategories();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Operation failed");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (category) => {
    setIsEditMode(true);
    setEditId(category.id);
    setName(category.name);
    setDescription(category.description || "");
    setNameError("");
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setEditId(null);
    setName("");
    setDescription("");
    setNameError("");
  };

  const handleDelete = async (category) => {
    if (category.docCount > 0) {
      toast.error(
        `Cannot delete "${category.name}" category because it is currently assigned to ${category.docCount} document(s). Reassign them first.`
      );
      return;
    }

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the category "${category.name}"?`
    );
    if (!confirmDelete) return;

    try {
      const { data } = await axios.delete(`${server}/categories/delete`, {
        headers: {
          Authorization: `Bearer ${token || localStorage.getItem("token")}`,
        },
        params: {
          id: category.id
        }
      });
      toast.success(data?.message || "Category deleted successfully!");
      fetchCategories();
      // If we are currently editing the deleted category, reset the form
      if (editId === category.id) {
        cancelEdit();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to delete category");
    }
  };

  // Dynamic Badge colors for category rows
  const getCategoryBadgeStyle = (catName) => {
    const text = catName.toLowerCase();
    if (text.includes("guide") || text.includes("how")) return "bg-teal-50 text-teal-700 border-teal-200";
    if (text.includes("api") || text.includes("tech")) return "bg-indigo-50 text-indigo-700 border-indigo-200";
    if (text.includes("design") || text.includes("ui")) return "bg-pink-50 text-pink-700 border-pink-200";
    if (text.includes("setup") || text.includes("install")) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 font-sans">
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="max-w-6xl mx-auto"
      >
        {/* Back navigation */}
        <button
          onClick={() => navigate("../docsList")}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 font-medium text-sm transition mb-6 group"
        >
          <FiArrowLeft className="group-hover:-translate-x-1 transition duration-200" />
          Back to documents
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-blue-50 text-blue-600 rounded-xl">
              <FiLayers className="text-2xl" />
            </span>
            Manage Categories
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Define and manage document tags, groups, and folders for the knowledge base.
          </p>
        </div>

        {/* Content Layout Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Left Column: Form Card */}
          <div className="lg:col-span-1 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
            <div className="flex items-center gap-2 font-bold text-slate-800 text-sm mb-4 border-b border-slate-100 pb-3">
              <FiFolder className="text-blue-500" />
              <span>{isEditMode ? "Edit Category Details" : "Create New Category"}</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Category Name */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. API Guides"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (e.target.value.trim()) setNameError("");
                  }}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition ${
                    nameError ? "border-red-300 focus:ring-red-500/10 focus:border-red-500" : "border-slate-200"
                  }`}
                />
                {nameError && (
                  <p className="text-red-500 text-xs mt-1.5 font-medium">{nameError}</p>
                )}
              </div>

              {/* Category Description */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  Description
                </label>
                <textarea
                  rows={4}
                  placeholder="Describe the type of documents contained in this category..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                {isEditMode && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="flex-1 px-4 py-2.5 border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-semibold text-slate-600 transition"
                  >
                    <FiX className="inline mr-1" /> Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={submitLoading}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 text-white px-4 py-2.5 rounded-xl text-xs font-bold shadow-sm transition"
                >
                  <FiCheck />
                  {submitLoading ? "Saving..." : isEditMode ? "Save Changes" : "Create"}
                </button>
              </div>
            </form>
          </div>

          {/* Right Column: Categories List Table */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100 text-slate-500 text-xs font-bold uppercase tracking-wider">
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Description</th>
                    <th className="px-6 py-4 text-center">Docs Count</th>
                    <th className="px-6 py-4 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-600">
                  {loading ? (
                    // Loader
                    Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-6 py-4">
                          <div className="h-5 bg-slate-100 rounded-full w-20" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-4 bg-slate-100 rounded w-48" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-5 bg-slate-100 rounded-full w-8 mx-auto" />
                        </td>
                        <td className="px-6 py-4">
                          <div className="h-8 bg-slate-100 rounded w-16 mx-auto" />
                        </td>
                      </tr>
                    ))
                  ) : categories.length === 0 ? (
                    // Empty list state
                    <tr>
                      <td colSpan={4} className="px-6 py-12 text-center text-slate-400">
                        <FiLayers className="text-3xl mx-auto mb-2 text-slate-300" />
                        <p className="text-xs">No categories defined yet.</p>
                      </td>
                    </tr>
                  ) : (
                    categories.map((cat) => (
                      <tr key={cat.id} className="hover:bg-slate-50/50 transition">
                        {/* Name */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-bold border ${getCategoryBadgeStyle(cat.name)}`}>
                            {cat.name}
                          </span>
                        </td>
                        {/* Description */}
                        <td className="px-6 py-4 text-slate-500 max-w-xs truncate">
                          {cat.description || "No description provided."}
                        </td>
                        {/* Doc Count */}
                        <td className="px-6 py-4 text-center">
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                            {cat.docCount}
                          </span>
                        </td>
                        {/* Actions */}
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleEdit(cat)}
                              title="Edit Category"
                              className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-blue-600 rounded-lg transition"
                            >
                              <FiEdit2 className="text-sm" />
                            </button>
                            <button
                              onClick={() => handleDelete(cat)}
                              title="Delete Category"
                              className="p-1.5 hover:bg-slate-100 text-slate-500 hover:text-red-600 rounded-lg transition"
                            >
                              <FiTrash2 className="text-sm" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Hint message box */}
            <div className="px-6 py-4 bg-slate-50/50 border-t border-slate-100 flex items-start gap-2.5 text-xs text-slate-400">
              <FiInfo className="text-blue-500/70 text-base shrink-0 mt-0.5" />
              <p className="leading-relaxed">
                <strong>Cascading updates</strong>: Editing a category name automatically updates all document records previously categorized under the old name. 
                <br />
                <strong>Referential integrity</strong>: Categories currently in use by one or more documents cannot be deleted.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
